/**
 * Org Dashboard (DEV-only, read-only)
 *
 * Doctrine: ROLE_ARCHITECTURE.md, MASTER_OPERATING_MODEL.md Layer 4
 * - Shows org name, member counts, basic stats placeholders
 * - Uses organization + membership table reads only
 * - No writes, no competitive engine interaction
 */

import { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import PaywallGate from '@/components/PaywallGate';
import SkeletonRow from '@/components/SkeletonRow';
import type { Organization, OrganizationMembership, OrgRole } from '@/types/database';

interface OrgWithStats {
  org: Organization;
  myRole: OrgRole;
  memberCount: number;
}

export default function OrgDashboardScreen() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const c = theme.colors;
  const [orgData, setOrgData] = useState<OrgWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadOrgs = useCallback(async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch my memberships
      const { data: memberships, error: memErr } = await supabase
        .from('organization_memberships')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: true });

      if (memErr) {
        // Table may not exist yet (pre-migration)
        setError(`Memberships: ${memErr.message}`);
        setLoading(false);
        return;
      }

      const myMemberships = (memberships as OrganizationMembership[]) ?? [];
      if (myMemberships.length === 0) {
        setOrgData([]);
        setLoading(false);
        return;
      }

      const orgIds = [...new Set(myMemberships.map((m) => m.org_id))];

      // Fetch org details
      const { data: orgs, error: orgErr } = await supabase
        .from('organizations')
        .select('*')
        .in('id', orgIds);

      if (orgErr) {
        setError(`Organizations: ${orgErr.message}`);
        setLoading(false);
        return;
      }

      const orgList = (orgs as Organization[]) ?? [];

      // Build stats per org
      const result: OrgWithStats[] = [];
      for (const org of orgList) {
        const myMembership = myMemberships.find((m) => m.org_id === org.id);
        if (!myMembership) continue;

        // Count members (only if I'm admin/owner)
        let memberCount = 0;
        if (myMembership.role === 'owner' || myMembership.role === 'admin') {
          const { count } = await supabase
            .from('organization_memberships')
            .select('id', { count: 'exact', head: true })
            .eq('org_id', org.id)
            .eq('status', 'active');
          memberCount = count ?? 0;
        }

        result.push({
          org,
          myRole: myMembership.role as OrgRole,
          memberCount,
        });
      }

      setOrgData(result);
    } catch (e: any) {
      setError(e.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  return (
    <PaywallGate requiredTier="org_pro">
    <ScrollView style={[styles.container, { backgroundColor: c.background }]}>
      <Text style={[styles.title, { color: c.accent }]}>ORG DASHBOARD</Text>
      <Text style={[styles.subtitle, { color: c.textSecondary }]}>
        Read-only org membership view (DEV)
      </Text>

      {loading && <SkeletonRow rows={3} />}

      {error && (
        <View style={[styles.errorCard, { backgroundColor: c.surfaceVariant }]}>
          <Text style={[styles.errorText, { color: c.error || '#ff4444' }]}>
            {error}
          </Text>
          <Text style={[styles.errorHint, { color: c.textTertiary }]}>
            Tables may not exist yet. Run org-agnostic schema migration first.
          </Text>
        </View>
      )}

      {!loading && !error && orgData.length === 0 && (
        <Text style={[styles.emptyText, { color: c.textSecondary }]}>
          No organization memberships found.
        </Text>
      )}

      {orgData.map((item) => (
        <View
          key={item.org.id}
          style={[styles.orgCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <View style={styles.orgHeader}>
            <Text style={[styles.orgName, { color: c.text }]}>{item.org.name}</Text>
            <Text style={[styles.orgType, { color: c.accent }]}>
              {item.org.org_type.toUpperCase()}
            </Text>
          </View>
          <View style={styles.orgMeta}>
            <Text style={[styles.metaText, { color: c.textSecondary }]}>
              My role: {item.myRole}
            </Text>
            {(item.myRole === 'owner' || item.myRole === 'admin') && (
              <Text style={[styles.metaText, { color: c.textSecondary }]}>
                Members: {item.memberCount}
              </Text>
            )}
            <Text style={[styles.metaText, { color: c.textTertiary }]}>
              Created: {new Date(item.org.created_at).toLocaleDateString()}
            </Text>
          </View>
          <View style={styles.statsPlaceholder}>
            <Text style={[styles.placeholderText, { color: c.textTertiary }]}>
              Aggregate reporting available in Club dashboard
            </Text>
          </View>
        </View>
      ))}
    </ScrollView>
    </PaywallGate>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, paddingTop: 60 },
  title: { fontSize: 24, fontWeight: '900', letterSpacing: 2, marginBottom: 4 },
  subtitle: { fontSize: 14, marginBottom: 20 },
  loader: { marginTop: 40 },
  errorCard: { padding: 16, borderRadius: 2, marginBottom: 16 },
  errorText: { fontSize: 13, fontWeight: '600', fontFamily: Platform.OS === 'web' ? 'monospace' : undefined },
  errorHint: { fontSize: 12, marginTop: 8 },
  emptyText: { fontSize: 16, textAlign: 'center', marginTop: 40 },
  orgCard: { borderWidth: 1, borderRadius: 2, padding: 16, marginBottom: 12 },
  orgHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  orgName: { fontSize: 18, fontWeight: '700', flex: 1 },
  orgType: { fontSize: 12, fontWeight: '700', letterSpacing: 1 },
  orgMeta: { gap: 4, marginBottom: 12 },
  metaText: { fontSize: 13 },
  statsPlaceholder: { padding: 12, borderRadius: 2, backgroundColor: 'rgba(128,128,128,0.1)' },
  placeholderText: { fontSize: 12, textAlign: 'center', fontStyle: 'italic' },
});
